import { useLang } from "@/lib/i18n";
import { weekDays, parseWeek, serializeWeek, weekSummary } from "@/lib/hours";
import HoursDayRow from "@/components/yts/hours/HoursDayRow";
import HoursPill from "@/components/yts/hours/HoursPill";
import HoursAIFill from "@/components/yts/hours/HoursAIFill";

const DEFAULT_HOURS = { open: "11:00", close: "21:00" };

/**
 * A business's week, day by day — the one editor behind the site team's screens
 * and the owner's own. Whatever is set here becomes the schedule the site reads;
 * the pill underneath shows exactly what a visitor will see, and the written
 * line is filled from the days above unless the place keeps irregular hours.
 */
export default function HoursEditor({
  hours,
  line,
  onChange,
  inputCls = "",
  labelCls = "block font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#6d6d6a] mb-1.5",
}) {
  const { lang, t } = useLang();
  const days = weekDays(lang);
  const week = parseWeek(hours);
  // A day reopened after being shut starts from the week's own hours.
  const seed = days.map((d) => week?.[d.key]).find(Boolean) || DEFAULT_HOURS;
  const today = days[0];

  const setWeek = (next) =>
    onChange({ hours: serializeWeek(next), line: weekSummary(next, lang, t("hours.daily")) || line || "" });
  const setDay = (key, value) => setWeek({ ...(week || {}), [key]: value });
  const toggleDay = (key) => setDay(key, week?.[key] ? null : { ...seed });
  const applyToAll = () => {
    const first = week?.[today.key];
    if (!first) return;
    setWeek(Object.fromEntries(days.map((d) => [d.key, { ...first }])));
  };

  return (
    <div className="rounded-[14px] border border-[#deded9] bg-white p-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <div className={labelCls}>{t("hours.editTitle")}</div>
        {week?.[today.key] && (
          <button type="button" onClick={applyToAll} className="text-[11px] font-bold text-[#6d6d6a] hover:text-[#171717] hover:underline">
            {t("hours.applyAll", { day: today.label })}
          </button>
        )}
      </div>
      <p className="mt-1 text-[11.5px] text-[#8d8d88] leading-[1.45]">{t("hours.editHint")}</p>

      {/* Faster than seven rows when the week is one sentence long. */}
      <HoursAIFill inputCls={inputCls} onApply={onChange} />

      <div className="mt-2.5">
        {days.map((day) => (
          <HoursDayRow
            key={day.key}
            day={day}
            hours={week?.[day.key]}
            onHours={(value) => setDay(day.key, value)}
            onToggle={() => toggleDay(day.key)}
            inputCls={inputCls}
            label={`${day.label} · ${t("hours.closedDay")}`}
          />
        ))}
      </div>

      <div className="mt-3.5 border-t border-[#ededec] pt-3.5">
        <label className={labelCls}>{t("hours.lineLabel")}</label>
        <input
          className={inputCls}
          value={line || ""}
          onChange={(e) => onChange({ hours: serializeWeek(week), line: e.target.value })}
          placeholder={lang === "es" ? "Mar–dom 11am–9pm" : "Tue–Sun 11am–9pm"}
        />
        <div className="mt-1 text-[11px] text-[#8d8d88]">{t("hours.lineHint")}</div>
      </div>

      <div className="mt-3.5 border-t border-[#ededec] pt-3.5">
        <div className="font-mono text-[9px] tracking-[0.18em] uppercase font-bold text-[#8d8d88] mb-1.5">
          {t("hours.preview")}
        </div>
        <HoursPill week={week} line={line} />
      </div>
    </div>
  );
}